import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SawDust } from '@cudirect/sawdust/sawdust';

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly logger = inject(SawDust);

  /**
   * Open a new tab and display file.
   * @param file Blob - Blob of file.
   * @param mimeType string - Mime Type of file.
   */
  public downloadFile(file: Blob, mimeType: string): void {
    if (!this.isBrowser) return;
    const image = new Blob([file], { type: mimeType });
    const fileURL = window.URL.createObjectURL(image);
    window.open(fileURL, '_blank');
  }

  public downloadFileToiFrame(file: Blob, mimeType: string): string {
    if (!this.isBrowser) return '';
    const image = new Blob([file], { type: mimeType });
    const fileURL = window.URL.createObjectURL(image);
    return fileURL;
  }

  /**
   * Handles errors by logging them to the console.
   * NOTE: Retry interceptor shoud handle any UI elements being displayed in error states in most cases.
   *
   * @param error - The error object to be logged.
   * @returns void
   */
  public errorHandling(error: Error): void {
    this.logger.withError(error).error('CommonService encountered an error', { component: 'CommonService' });
  }

  /** sanitize Filename */
  public sanitizeFilename(filename: string): string {
    // List of reserved Windows filenames
    const reservedNames = [
      'CON',
      'PRN',
      'AUX',
      'NUL',
      'COM1',
      'COM2',
      'COM3',
      'COM4',
      'COM5',
      'COM6',
      'COM7',
      'COM8',
      'COM9',
      'LPT1',
      'LPT2',
      'LPT3',
      'LPT4',
      'LPT5',
      'LPT6',
      'LPT7',
      'LPT8',
      'LPT9'
    ];

    // Remove the list of special characters from the filename and trim if any.
    // Use bounded quantifiers to prevent ReDoS attacks
    // Allow alphanumeric characters and dots (for file extensions)
    let sanitized = filename.replace(/[^a-zA-Z0-9.]{1,100}/g, '_').replace(/_{2,}/g, '_').replace(/(?:^_{1,50}|_{1,50}$)/g, '');

    // Check if the original filename (before sanitization) contains a reserved name
    // This handles cases like "___CON___.txt" where CON should be detected
    const cleanOriginal = filename.replace(/[^a-zA-Z0-9.]/g, '');
    const originalBaseName = cleanOriginal.split('.').slice(0, -1).join('.') || cleanOriginal;
    if (reservedNames.includes(originalBaseName.toUpperCase())) {
      // If we found a reserved name in the original, create a clean version with safe_ prefix
      const extension = cleanOriginal.includes('.') ? cleanOriginal.split('.').pop() : '';
      sanitized = 'safe_' + originalBaseName + (extension ? '.' + extension : '');
    } else {
      // Fallback: check sanitized filename for reserved names
      const baseName = sanitized.split('.').slice(0, -1).join('.') || sanitized;
      if (reservedNames.includes(baseName.toUpperCase())) {
        sanitized = 'safe_' + sanitized;
      }
    }

    // Trim filename to 255 characters
    if (sanitized.length > 255) {
      const extension = sanitized.includes('.') ? sanitized.split('.').pop() : '';
      const base = sanitized.slice(0, 255 - (extension ? extension.length + 1 : 0));
      sanitized = base + (extension ? '.' + extension : '');
    }

    return sanitized;
  }
}
